import React, { useState, useEffect } from "react";
import api, { petAPI } from "../../services/api";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";

// Lightweight modal for creating a Pet Guardian inline while admitting an outpatient
// Mirrors the reception modal styling used across the app
const AddPetOwnerModal = ({ isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    ownerFirstName: "",
    ownerLastName: "",
    ownerEmail: "",
    petName: "",
    species: "",
    breed: "",
    roleId: "",
  });

  const [speciesList, setSpeciesList] = useState([]);
  const [breedList, setBreedList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset form each time the modal opens
      setForm({
        ownerFirstName: "",
        ownerLastName: "",
        ownerEmail: "",
        petName: "",
        species: "",
        breed: "",
        roleId: "",
      });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Load pet species options
  useEffect(() => {
    petAPI
      .getPetsBySpecies(null, { dropdown: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setSpeciesList(list);
      })
      .catch(() => setSpeciesList([]));
  }, []);

  // Load breeds whenever species changes
  useEffect(() => {
    if (!form.species) {
      setBreedList([]);
      setForm((prev) => ({ ...prev, breed: "" }));
      return;
    }
    petAPI
      .getBreedBySpecies(form.species, { include_all: true, dropdown: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setBreedList(list);
      })
      .catch(() => setBreedList([]));
  }, [form.species]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const payload = {
      first_name: form.ownerFirstName,
      last_name: form.ownerLastName,
      email: form.ownerEmail,
      pet_species: form.species,
      pet_breed: form.breed,
      pet_name: form.petName,
    };

    try {
      setSaving(true);
      const res = await api.post('/api/v1/pet-owner-medical-account-register', payload);
      showSuccessToast('Pet Guardian registered successfully');
      if (onSave) onSave(payload, res?.data);
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to register Pet Guardian';
      showErrorToast(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="reception-modal-overlay">
      <div className="reception-modal" style={{ maxWidth: "640px" }}>
        <div className="reception-modal-header">
          <span className="reception-modal-title">New Pet Guardian Registration</span>
          <button className="reception-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form className="reception-modal-form" onSubmit={(e) => e.preventDefault()}>
          <label className="reception-modal-label">
            First Name
            <input
              type="text"
              className="reception-modal-input"
              value={form.ownerFirstName}
              onChange={(e) => handleChange("ownerFirstName", e.target.value)}
              placeholder="Enter Pet Guardian first name"
            />
          </label>

          <label className="reception-modal-label">
            Last Name
            <input
              type="text"
              className="reception-modal-input"
              value={form.ownerLastName}
              onChange={(e) => handleChange("ownerLastName", e.target.value)}
              placeholder="Enter Pet Guardian last name"
            />
          </label>
          <label className="reception-modal-label">
            Pet Guardian email
            <input
              type="email"
              className="reception-modal-input"
              value={form.ownerEmail}
              onChange={(e) => handleChange("ownerEmail", e.target.value)}
              placeholder="name@example.com"
            />
          </label>

          <label className="reception-modal-label">
            Pet name
            <input
              type="text"
              className="reception-modal-input"
              value={form.petName}
              onChange={(e) => handleChange("petName", e.target.value)}
              placeholder="Enter Pet name"
            />
          </label>

          <label className="reception-modal-label">
            Pet species
            <select
              className="reception-modal-input"
              value={form.species}
              onChange={(e) => handleChange("species", e.target.value)}
            >
              <option value="">Select species</option>
              {speciesList.map((s) => (
                <option key={s.id || s.name} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <label className="reception-modal-label">
            Pet breed
            <select
              className="reception-modal-input"
              value={form.breed}
              onChange={(e) => handleChange("breed", e.target.value)}
              disabled={!form.species}
            >
              <option value="">{form.species ? "Select breed" : "Select species first"}</option>
              {breedList.map((b) => (
                <option key={b.id || b.name} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <div className="reception-modal-actions">
            <button type="button" className="reception-action-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="reception-action-btn cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPetOwnerModal;


